import { useState } from "react";
import { Link } from "react-router";
import { BatchResult } from "../../components/BatchResult";
import { FilterChips } from "../../components/FilterChips";
import { Modal } from "../../components/Modal";
import { Pagination } from "../../components/Pagination";
import { SearchInput } from "../../components/SearchInput";
import { SelectionBar } from "../../components/SelectionBar";
import { SuspendCasesModal } from "../../components/SuspendCasesModal";
import {
  Alert,
  Button,
  Card,
  Dropdown,
  EmptyState,
  Field,
  MENU_ITEM,
  MENU_ITEM_DANGER,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Textarea,
  Th,
} from "../../components/ui";
import { useCanWrite } from "../../auth/AuthProvider";
import { formatDateTime, formatRelative } from "../../lib/format";
import { useDecideRiskCases, useNoteRiskCases, useRiskCases, useRiskRules, useRiskSweep } from "../../lib/queries";
import { PRIORITY_LABEL, PRIORITY_TONE, SNOOZES } from "../../lib/risk";
import type { BatchOutcome, RiskCase, RiskPriority } from "../../lib/types";

const LIMIT = 50;

type View = "active" | "snoozed" | "cleared" | "suspended";
const VIEWS: { value: Exclude<View, "active">; label: string }[] = [
  { value: "snoozed", label: "Snoozed" },
  { value: "cleared", label: "Cleared" },
  { value: "suspended", label: "Suspended" },
];
const PRIORITIES: { value: RiskPriority; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
];

/** A menu in the selection bar, drawn like the secondary buttons beside it. */
const BAR_MENU =
  "h-8 rounded-md border border-line px-2.5 text-sm text-fg hover:border-line-strong hover:bg-raised pointer-coarse:h-auto pointer-coarse:min-h-11";

const snoozedNow = (row: RiskCase) => Boolean(row.snoozedUntil && new Date(row.snoozedUntil) > new Date());

/**
 * The review queue -- what looks like abuse, one file per organization,
 * critical first, then by score. Nothing here happened automatically to a
 * customer: every row is waiting on, or records, a person's decision.
 *
 * Built to be worked, not just read. The filters narrow to what one
 * reviewer is dealing with -- a priority, a rule, "mine"; a row can be
 * claimed without opening it; and several rows of the same shape can be
 * moved at once -- claimed, handed back, snoozed, noted, cleared with one
 * note -- which is how a morning of "database published with no allowlist"
 * gets handled in a minute rather than an hour.
 *
 * Suspension is in the bar too, one step further away than the rest: under
 * "More", behind the operator's authenticator, with one reason every tenant
 * is shown and mailed. It exists for the signup burst that turns out to be
 * one abuser under twenty names; a single tenant is still suspended from its
 * own case, where the evidence is.
 *
 * Support can select as well, for the one thing they may do to a case:
 * leave a note on it.
 */
export function CasesPage() {
  const canWrite = useCanWrite();
  const [view, setView] = useState<Exclude<View, "active"> | "">("");
  const [priority, setPriority] = useState<RiskPriority | "">("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const [offset, setOffset] = useState(0);
  const status: View = view === "" ? "active" : view;
  const filters = {
    status,
    ...(priority ? { priority } : {}),
    ...(kind ? { kind } : {}),
    ...(q ? { q } : {}),
    ...(mine ? { mine: true } : {}),
    limit: LIMIT,
    offset,
  };
  const { data, isPending, error } = useRiskCases(filters);
  const rules = useRiskRules();
  const sweep = useRiskSweep();
  const move = useDecideRiskCases();
  const note = useNoteRiskCases();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [dialog, setDialog] = useState<null | "clear" | "note" | "suspend">(null);
  const [bulkNote, setBulkNote] = useState("");
  const [result, setResult] = useState<{ outcomes: BatchOutcome[]; done: string } | null>(null);

  const open = status === "active" || status === "snoozed";
  const ruleTitle = (k: string) => rules.data?.get(k)?.title ?? k;
  /* Selection is one page's and says so: it is cleared whenever the page
     under it changes, rather than reaching rows nobody can see. */
  const reset = () => {
    setOffset(0);
    setSelected(new Set());
    setResult(null);
  };
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = data?.data ?? [];
  const chosen = rows.filter((row) => selected.has(row.id));
  const allOnPage = rows.length > 0 && rows.every((row) => selected.has(row.id));
  const claimed = chosen.filter((row) => row.status === "reviewing");
  const parked = chosen.filter(snoozedNow);
  const critical = chosen.filter((row) => row.priority === "critical").length;
  const distinctRules = new Set(chosen.flatMap((row) => row.kinds)).size;

  /* Every batch ends the same way: the answer on the page, the selection
     and any dialog gone. */
  const finish = (done: string) => (response: { data: BatchOutcome[] }) => {
    setResult({ outcomes: response.data, done });
    setSelected(new Set());
    setDialog(null);
    setBulkNote("");
  };
  const run = (
    ids: string[],
    input: { status: "open" | "reviewing" | "cleared"; note?: string; snoozeHours?: number },
    done: string,
  ) => move.mutate({ ids, ...input }, { onSuccess: finish(done) });
  const closeDialog = () => {
    if (move.isPending || note.isPending) return;
    setDialog(null);
  };

  return (
    <>
      <PageHeader
        title="Cases"
        description="Organizations whose activity looks like abuse, for a person to read and decide. Nothing is suspended until somebody here says so."
        action={
          canWrite ? (
            <Button variant="secondary" loading={sweep.isPending} onClick={() => sweep.mutate()}>
              Run the rules now
            </Button>
          ) : undefined
        }
      />
      {sweep.data && (
        <Alert tone="info">
          {sweep.data.filed === 0 ? "Nothing new to file." : `${sweep.data.filed} new signal${sweep.data.filed === 1 ? "" : "s"} filed.`}
        </Alert>
      )}
      {sweep.error && <Alert>{sweep.error.message}</Alert>}
      {result && <BatchResult outcomes={result.outcomes} noun="case" done={result.done} />}
      {move.error && dialog === null && <Alert>{move.error.message}</Alert>}

      <Card>
        {/* Two rows of controls: which list, then what within it. The
            second row narrows the first; neither widens anything. */}
        <div className="space-y-3 border-b border-line px-5 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <FilterChips
              value={view}
              onChange={(next) => {
                setView(next);
                reset();
              }}
              options={VIEWS}
              allLabel="Open"
              ariaLabel="Which cases"
            />
            <div className="sm:w-64">
              <SearchInput
                value={q}
                onChange={(next) => {
                  setQ(next);
                  reset();
                }}
                placeholder="Organization…"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <FilterChips
              value={priority}
              onChange={(next) => {
                setPriority(next);
                reset();
              }}
              options={PRIORITIES}
              allLabel="Any priority"
              ariaLabel="Priority"
            />
            <Select
              value={kind}
              onChange={(event) => {
                setKind(event.target.value);
                reset();
              }}
              aria-label="Rule"
              className="sm:max-w-72"
            >
              <option value="">Any rule</option>
              {[...(rules.data?.values() ?? [])].map((rule) => (
                <option key={rule.kind} value={rule.kind}>
                  {rule.title}
                </option>
              ))}
            </Select>
            {open && (
              <label className="flex items-center gap-2 text-sm text-fg">
                <input
                  type="checkbox"
                  className="size-3.5 accent-accent"
                  checked={mine}
                  onChange={(event) => {
                    setMine(event.target.checked);
                    reset();
                  }}
                />
                Mine
              </label>
            )}
          </div>
        </div>

        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon="shield"
            title={
              status === "active"
                ? mine
                  ? "Nothing claimed by you"
                  : q || priority || kind
                    ? "Nothing matches"
                    : "Nothing waiting"
                : `No ${status} cases`
            }
            description={
              status === "active" && !q && !priority && !kind && !mine
                ? "The rules run every five minutes. A case appears here the moment one fires."
                : undefined
            }
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  {open && (
                    <Th className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select every case on this page"
                        checked={allOnPage}
                        onChange={() => setSelected(allOnPage ? new Set() : new Set(rows.map((row) => row.id)))}
                        className="size-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                      />
                    </Th>
                  )}
                  <Th>Organization</Th>
                  <Th numeric>Score</Th>
                  <Th>What fired</Th>
                  <Th priority="md">{open ? "Latest" : "Decided"}</Th>
                  <Th priority="lg">{open ? "Reviewer" : "By"}</Th>
                  {canWrite && open && <Th className="text-right"> </Th>}
                </tr>
              }
            >
              {data.data.map((row) => (
                <CaseRow
                  key={row.id}
                  row={row}
                  open={open}
                  canWrite={canWrite}
                  selected={selected.has(row.id)}
                  onToggle={() => toggle(row.id)}
                  // A row's own claim leaves the selection alone: it is not a batch.
                  onClaim={() =>
                    move.mutate(
                      { ids: [row.id], status: "reviewing" },
                      { onSuccess: (response) => setResult({ outcomes: response.data, done: "claimed" }) },
                    )
                  }
                  claiming={move.isPending && move.variables?.ids.length === 1 && move.variables.ids[0] === row.id}
                  ruleTitle={ruleTitle}
                />
              ))}
            </Table>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={(next) => {
                setOffset(next);
                setSelected(new Set());
              }}
            />
          </>
        )}
      </Card>

      <p className="px-1 text-xs leading-6 text-fg-subtle">
        The score weighs each signal by severity, lets it fade over seven days, counts repeats with a
        ceiling, caps any one rule, and compounds across areas that corroborate each other. Each case
        shows its working. A critical signal also mails staff at once. Clearing a case closes it; the
        next signal on that organization opens a new one.
      </p>

      {open && (
        <SelectionBar
          count={chosen.length}
          noun="case"
          onClear={() => setSelected(new Set())}
          action={
            canWrite ? (
              <span className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={move.isPending && (move.variables?.ids.length ?? 0) > 1 && move.variables?.status === "reviewing"}
                  onClick={() => run(chosen.map((c) => c.id), { status: "reviewing" }, "claimed")}
                >
                  Claim
                </Button>
                {/* Snoozing hands a claimed case back as it parks it, as on
                    the case's own page; waking is offered for the ones that
                    are parked. */}
                <Dropdown side="top" align="end" trigger={<span>Snooze ▾</span>} triggerClassName={BAR_MENU}>
                  {SNOOZES.map((option) => (
                    <button
                      key={option.hours}
                      type="button"
                      className={MENU_ITEM}
                      onClick={() => run(chosen.map((c) => c.id), { status: "open", snoozeHours: option.hours }, "snoozed")}
                    >
                      {option.label}
                    </button>
                  ))}
                  {parked.length > 0 && (
                    <button
                      type="button"
                      className={MENU_ITEM}
                      onClick={() => run(parked.map((c) => c.id), { status: "open" }, "woken")}
                    >
                      Wake {parked.length} now
                    </button>
                  )}
                </Dropdown>
                <Button size="sm" onClick={() => setDialog("clear")}>
                  Clear…
                </Button>
                <Dropdown side="top" align="end" trigger={<span>More ▾</span>} triggerClassName={BAR_MENU}>
                  {/* Only the claimed ones: handing back an unclaimed case
                      would write "handed it back" on a file nobody held. */}
                  {claimed.length > 0 && (
                    <button
                      type="button"
                      className={MENU_ITEM}
                      onClick={() => run(claimed.map((c) => c.id), { status: "open" }, "handed back")}
                    >
                      Hand back {claimed.length}
                    </button>
                  )}
                  <button type="button" className={MENU_ITEM} onClick={() => setDialog("note")}>
                    Add a note…
                  </button>
                  <button type="button" className={MENU_ITEM_DANGER} onClick={() => setDialog("suspend")}>
                    Suspend organizations…
                  </button>
                </Dropdown>
              </span>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setDialog("note")}>
                Add a note…
              </Button>
            )
          }
        >
          {critical > 0
            ? `${critical} critical among them — read those first`
            : `${distinctRules} distinct rule${distinctRules === 1 ? "" : "s"}`}
        </SelectionBar>
      )}

      <Modal
        open={dialog === "clear"}
        onClose={closeDialog}
        title={`Clear ${chosen.length} case${chosen.length === 1 ? "" : "s"}`}
        description="One note on all of them. The next signal on any of these organizations opens a new case."
        dismissable={!move.isPending}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            run(chosen.map((c) => c.id), { status: "cleared", note: bulkNote.trim() }, "cleared");
          }}
          className="space-y-4"
        >
          {move.error && <Alert>{move.error.message}</Alert>}
          <CaseList cases={chosen} ruleTitle={ruleTitle} />
          <Field label="Why" htmlFor="bulk-note" hint="For the next reviewer. Kept on every case and in the audit log.">
            <Textarea id="bulk-note" rows={3} value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="Published databases with allowlists documented in their tickets." required />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeDialog} disabled={move.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={move.isPending && move.variables?.status === "cleared"}>
              Clear {chosen.length}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={dialog === "note"}
        onClose={closeDialog}
        title={`Add a note to ${chosen.length} case${chosen.length === 1 ? "" : "s"}`}
        description="Decides nothing. The same note sits on each case's timeline for the next reviewer, and in the audit log."
        dismissable={!note.isPending}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            note.mutate({ ids: chosen.map((c) => c.id), note: bulkNote.trim() }, { onSuccess: finish("noted") });
          }}
          className="space-y-4"
        >
          {note.error && <Alert>{note.error.message}</Alert>}
          <CaseList cases={chosen} ruleTitle={ruleTitle} />
          <Field label="Note" htmlFor="bulk-case-note">
            <Textarea
              id="bulk-case-note"
              rows={3}
              maxLength={2000}
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              placeholder="Same signup burst as the others: one card, twelve accounts."
              required
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeDialog} disabled={note.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={note.isPending} disabled={bulkNote.trim().length === 0}>
              Add to {chosen.length}
            </Button>
          </div>
        </form>
      </Modal>

      <SuspendCasesModal
        open={dialog === "suspend"}
        cases={chosen}
        onClose={() => setDialog(null)}
        onDone={(outcomes) => finish("suspended")({ data: outcomes })}
      />
    </>
  );
}

/** The selection, restated inside the dialog that acts on it. */
function CaseList({ cases, ruleTitle }: { cases: RiskCase[]; ruleTitle: (kind: string) => string }) {
  return (
    <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-fg-subtle">
      {cases.map((c) => (
        <li key={c.id}>
          <span className="text-fg">{c.organization.name}</span> · {c.kinds.map(ruleTitle).join(", ")}
        </li>
      ))}
    </ul>
  );
}

function CaseRow({
  row,
  open,
  canWrite,
  selected,
  onToggle,
  onClaim,
  claiming,
  ruleTitle,
}: {
  row: RiskCase;
  open: boolean;
  canWrite: boolean;
  selected: boolean;
  onToggle: () => void;
  onClaim: () => void;
  claiming: boolean;
  ruleTitle: (kind: string) => string;
}) {
  return (
    <tr className={`hover:bg-raised ${selected ? "bg-raised/60" : ""}`}>
      {open && (
        <Td>
          <input
            type="checkbox"
            aria-label={`Select ${row.organization.name}`}
            checked={selected}
            onChange={onToggle}
            className="size-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
          />
        </Td>
      )}
      <Td>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/safety/${row.id}`} className="font-medium text-accent hover:underline">
            {row.organization.name}
          </Link>
          {row.priority !== "normal" && <Tag tone={PRIORITY_TONE[row.priority]}>{PRIORITY_LABEL[row.priority]}</Tag>}
          {row.organization.suspendedAt && <Tag tone="neutral">Suspended</Tag>}
          {snoozedNow(row) && <Tag tone="neutral">Snoozed until {formatDateTime(row.snoozedUntil)}</Tag>}
        </div>
        {/* The one line that says what this is, so the queue reads as a
            list of problems rather than a list of names. */}
        {row.topSummary && <p className="mt-0.5 max-w-lg truncate text-xs text-fg-subtle" title={row.topSummary}>{row.topSummary}</p>}
      </Td>
      <Td numeric>
        <span className={row.priority === "critical" ? "font-medium text-danger" : row.priority === "high" ? "font-medium text-warn" : undefined}>
          {row.score}
        </span>
      </Td>
      <Td>
        <p className="max-w-md truncate text-sm text-fg" title={row.kinds.map(ruleTitle).join(" · ")}>
          {row.kinds.map(ruleTitle).join(" · ")}
        </p>
        <p className="text-xs text-fg-subtle">
          {row.counts.signals} signal{row.counts.signals === 1 ? "" : "s"}
          {row.counts.critical > 0 ? `, ${row.counts.critical} critical` : ""} · opened {formatRelative(row.openedAt)}
        </p>
      </Td>
      <Td priority="md" className="whitespace-nowrap text-fg-subtle" title={formatDateTime(open ? row.lastSignalAt : row.decidedAt)}>
        {formatRelative(open ? row.lastSignalAt : row.decidedAt)}
      </Td>
      <Td priority="lg" className="text-fg-subtle">
        {open ? (row.assignedToMe ? "You" : row.assignedToName ?? "Unclaimed") : row.decidedByName ?? "—"}
      </Td>
      {canWrite && open && (
        <Td className="text-right">
          {row.status === "open" ? (
            <button type="button" disabled={claiming} onClick={onClaim} className="text-sm text-accent hover:underline disabled:opacity-50">
              {claiming ? "Claiming…" : "Claim"}
            </button>
          ) : (
            <Link to={`/safety/${row.id}`} className="text-sm text-accent hover:underline">
              Review
            </Link>
          )}
        </Td>
      )}
    </tr>
  );
}
